import { ReactElement, ReactNode, useRef } from 'react';
import clsx from 'clsx';

import { useIsOverflowing } from '@atom/hooks/useIsOverflowing';
import { Icon, Menu } from '@atom/mui';
import { ChevronRightIcon, MoreHorizIcon } from '@atom/mui/icons';
import { MediaType } from '@atom/types/media';

import { MEDIA_TYPE_LABELS } from './constants';
import { Breadcrumb } from './types';

import classes from './filesTab.module.css';

const { MenuItem } = Menu;

const addSeparators = (crumbs: ReactElement[]): ReactNode[] =>
  crumbs.flatMap((crumb, index) =>
    index === 0
      ? [crumb]
      : [
          <Icon
            key={`separator-${crumb.key}`}
            size="sm"
            icon={ChevronRightIcon}
          />,
          crumb,
        ],
  );

type FolderBreadcrumbsProps = {
  breadcrumbs: Breadcrumb[];
  selectedType: MediaType | null;
  selectedCount?: number;
  onCrumbClick: (id: string, index: number) => void;
};

const FolderBreadcrumbs = ({
  breadcrumbs,
  selectedType,
  selectedCount,
  onCrumbClick,
}: FolderBreadcrumbsProps) => {
  const measureRef = useRef<HTMLDivElement>(null);
  const overflowing = useIsOverflowing(measureRef, [breadcrumbs]);

  if (selectedType) {
    return (
      <div className={classes.folderBreadcrumbs}>
        <div className={classes.breadcrumbTrack}>
          <div className={classes.mediaTotalsType}>
            {`${selectedCount ?? 0} ${MEDIA_TYPE_LABELS[selectedType]}`}
          </div>
        </div>
      </div>
    );
  }

  const lastIndex = breadcrumbs.length - 1;

  const renderCrumb = (crumb: Breadcrumb, index: number) => (
    <div
      key={crumb.id}
      className={clsx(
        classes.breadcrumbItem,
        index === lastIndex && classes.breadcrumbCurrent,
      )}
      onClick={() => onCrumbClick(crumb.id, index)}
    >
      {crumb.name}
    </div>
  );

  const fullCrumbs = breadcrumbs.map(renderCrumb);

  const visibleCrumbs =
    overflowing && breadcrumbs.length > 2
      ? [
          renderCrumb(breadcrumbs[0], 0),
          <Menu
            key="collapsed"
            icon={MoreHorizIcon}
            noMinWidth
            IconButtonProps={{ size: 'small' }}
          >
            {breadcrumbs.slice(1, lastIndex).map((crumb, index) => (
              <MenuItem
                key={crumb.id}
                onClick={() => onCrumbClick(crumb.id, index + 1)}
              >
                {crumb.name}
              </MenuItem>
            ))}
          </Menu>,
          renderCrumb(breadcrumbs[lastIndex], lastIndex),
        ]
      : fullCrumbs;

  return (
    <div className={classes.folderBreadcrumbs}>
      {/* Hidden full-width copy used only to measure available width. */}
      <div ref={measureRef} className={classes.breadcrumbMeasure}>
        {addSeparators(fullCrumbs)}
      </div>
      <div className={classes.breadcrumbTrack}>
        {addSeparators(visibleCrumbs)}
      </div>
    </div>
  );
};

export default FolderBreadcrumbs;
